/**
 * Dynamic Map Resource Loader Service
 * 
 * This service handles conditional loading of map resources (Mapbox GL JS, Leaflet)
 * only when they are actually needed, improving page performance.
 */

class MapResourceLoader {
  constructor() {
    this.loadedResources = new Set();
    this.loadingPromises = new Map();
    this.resourceUrls = {
      mapbox: {
        css: 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css',
        js: 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js'
      },
      leaflet: {
        css: 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css',
        js: 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
      }
    };
  }

  /**
   * Load Mapbox GL JS resources (CSS + JS)
   * @returns {Promise} Resolves when both resources are loaded
   */
  async loadMapbox() {
    if (this.loadedResources.has('mapbox')) {
      return Promise.resolve();
    }

    if (this.loadingPromises.has('mapbox')) {
      return this.loadingPromises.get('mapbox');
    }

    const loadPromise = this._loadMapboxResources();
    this.loadingPromises.set('mapbox', loadPromise);
    
    try {
      await loadPromise;
      this.loadedResources.add('mapbox');
      this.loadingPromises.delete('mapbox');
    } catch (error) {
      this.loadingPromises.delete('mapbox');
      throw error;
    }

    return loadPromise;
  }

  /**
   * Load Leaflet resources (CSS + JS)
   * @returns {Promise} Resolves when both resources are loaded
   */
  async loadLeaflet() {
    if (this.loadedResources.has('leaflet')) {
      return Promise.resolve();
    }

    if (this.loadingPromises.has('leaflet')) {
      return this.loadingPromises.get('leaflet');
    }

    const loadPromise = this._loadLeafletResources();
    this.loadingPromises.set('leaflet', loadPromise);
    
    try {
      await loadPromise;
      this.loadedResources.add('leaflet');
      this.loadingPromises.delete('leaflet');
    } catch (error) {
      this.loadingPromises.delete('leaflet');
      throw error;
    }

    return loadPromise;
  }

  /**
   * Check if a resource is already loaded
   * @param {string} resourceType - 'mapbox' or 'leaflet'
   * @returns {boolean}
   */
  isLoaded(resourceType) {
    return this.loadedResources.has(resourceType);
  }

  /**
   * Check if a resource is currently loading
   * @param {string} resourceType - 'mapbox' or 'leaflet'
   * @returns {boolean}
   */
  isLoading(resourceType) {
    return this.loadingPromises.has(resourceType);
  }

  /**
   * Load Mapbox CSS and JS resources
   * @private
   */
  async _loadMapboxResources() {
    const promises = [];

    // Load CSS
    if (!document.querySelector(`link[href="${this.resourceUrls.mapbox.css}"]`)) {
      promises.push(this._loadCSS(this.resourceUrls.mapbox.css));
    }

    // Load JS
    if (typeof mapboxgl === 'undefined') {
      promises.push(this._loadScript(this.resourceUrls.mapbox.js));
    }

    await Promise.all(promises);
  }

  /**
   * Load Leaflet CSS and JS resources
   * @private
   */
  async _loadLeafletResources() {
    const promises = [];

    // Load CSS
    if (!document.querySelector(`link[href="${this.resourceUrls.leaflet.css}"]`)) {
      promises.push(this._loadCSS(this.resourceUrls.leaflet.css));
    }

    // Load JS
    if (typeof L === 'undefined') {
      promises.push(this._loadScript(this.resourceUrls.leaflet.js));
    }

    await Promise.all(promises);
  }

  /**
   * Load a CSS file dynamically
   * @private
   * @param {string} href - CSS file URL
   * @returns {Promise}
   */
  _loadCSS(href) {
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }

  /**
   * Load a JavaScript file dynamically
   * @private
   * @param {string} src - JavaScript file URL
   * @returns {Promise}
   */
  _loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Preload resources in the background (for better UX)
   * @param {string} resourceType - 'mapbox' or 'leaflet'
   */
  preload(resourceType) {
    if (resourceType === 'mapbox') {
      this.loadMapbox().catch(() => {}); // Silent preload
    } else if (resourceType === 'leaflet') {
      this.loadLeaflet().catch(() => {}); // Silent preload
    }
  }

  /**
   * Get loading status for all resources
   * @returns {Object} Status object
   */
  getStatus() {
    return {
      mapbox: {
        loaded: this.isLoaded('mapbox'),
        loading: this.isLoading('mapbox')
      },
      leaflet: {
        loaded: this.isLoaded('leaflet'),
        loading: this.isLoading('leaflet')
      }
    };
  }
}

// Export singleton instance
export default new MapResourceLoader();
